import { useState, useEffect } from 'react'
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion'
import axios from 'axios'
import { trackEvent } from '../utils/mixpanel'
import {
  ArrowDown,
  ArrowUp,
  CalendarClock,
  Check,
  FileText,
  Laptop,
  LineChart,
  Search,
  SlidersHorizontal,
  TrendingUp,
  Users,
} from 'lucide-react'
import Footer from '../components/Footer'

const API_URL = import.meta.env.VITE_API_URL || ''

const BACKGROUND_TEXTURES = {
  softNeutral:
    'https://images.unsplash.com/photo-1737276745810-ce753d468a34?auto=format&fit=crop&w=2200&q=80',
  organic:
    'https://images.unsplash.com/photo-1624607702690-bd8dc7e4dbc4?auto=format&fit=crop&w=2200&q=80',
  original:
  'https://images.unsplash.com/photo-1655012325190-89c9153a563c?auto=format&fit=crop&w=2200&q=80',
}
const BACKGROUND_PATTERN_URL = BACKGROUND_TEXTURES.softNeutral
const HOW_IT_WORKS_BACKGROUND_MODE = 'image' // 'image' | 'color'
const HOW_IT_WORKS_BACKGROUND_IMAGE_URL = './corporate-lander/how-it-works.jpg'

// const HERO_IMAGE_URL =
//   'https://images.unsplash.com/photo-1704083043868-a23986597567?auto=format&fit=crop&w=1400&q=80'
const HERO_IMAGE_URL =
  './corporate-lander/corporate1.jpg'
// const COHORT_IMAGE_URL =
//   'https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1200&q=80'
const COHORT_IMAGE_URL =
  './corporate-lander/online.jpg'
const COHORT_IMAGE_URL2 =
  './corporate-lander/energizersession.jpg'
  // Not on git - directly uploaded to public folder on the server
const ENERGIZERS_PDF_URL = '/Energizers Nervous System Regulation & Leadership Lab - June Cohort_.pdf'
  const CUSTOM_PROGRAM_IMAGE_URL =
  './corporate-lander/custom.jpeg'
// const EVENTS_IMAGE_URL =
//   'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1200&q=80'
const EVENTS_IMAGE_URL =
  './corporate-lander/corporateevents.jpg'
// const OFFSITE_IMAGE_URL =
//   'https://images.unsplash.com/photo-1499904350460-6f53e5a9b2d0?auto=format&fit=crop&w=1200&q=80'
const OFFSITE_IMAGE_URL =
  './corporate-lander/corporateevents2.jpg'
// const LEADERSHIP_IMAGE_URL =
  // 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80'
  // const LEADERSHIP_IMAGE_URL =
  // './corporate-lander/offsite2.png'
  const LEADERSHIP_IMAGE_URL =
  './corporate-lander/corporate-other.jpg'
// const ORGANISATION_IMPACT_IMAGE_URL =
  // 'https://images.unsplash.com/photo-1473448912268-2022ce9509d8?auto=format&fit=crop&w=900&q=80'
const ORGANISATION_IMPACT_IMAGE_URL =
  './corporate-lander/5.jpg'
const PROCESS_STEPS = [
  {
    icon: Search,
    title: 'Consult & Assess',
    description:
      'We assess your company culture and capture baseline wellbeing metrics to identify friction points.',
  },
  {
    icon: SlidersHorizontal,
    title: 'Custom Design',
    description:
      'Tailoring the exact mix of digital labs (The Energizers), daily tracks (The Digital Suite), and physical access passes.',
  },
  {
    icon: TrendingUp,
    title: 'Experience & Elevate',
    description:
      "Executing the program and delivering hard data on the positive shifts in your team's energy, focus, and stress levels.",
  },
]

const ORGANISATION_IMPACT_POINTS = [
  {
    title: 'Improved Focus & Productivity',
  },
  {
    title: 'Reduced Burnout & Absenteeism',
  },
  {
    title: 'Enhanced Team Retention & Morale',
  },
]

const ORGANISATION_STATS = [
  { label: 'Stress', value: '2.87', trend: 'down', tone: 'blue' },
  { label: 'Energy Levels', value: '0.70', trend: 'up', tone: 'green' },
  { label: 'Emotional State', value: '0.86', trend: 'up', tone: 'blue' },
  { label: 'Physical State', value: '1.04', trend: 'up', tone: 'green' },
]

const COHORT_FEATURED_FORMAT = {
  title: '',
  cadence: '12 online live sessions',
  bullets: [
    
    'Themed week-by-week progression',
    'Guided breathwork, mobility, and recovery',
    'Pre and post wellbeing tracking',
  ],
}

const DIGITAL_SUITE = {
  title: 'The Digital Suite: Daily Accountability',
  description:
    'Frictionless, daily digital infrastructure for remote and hybrid teams. Keep your team consistent with zero-barrier entry—just put headphones in and reset.',
  offerings: [
    {
      lead: 'The Mid-Day Reset',
      text: '30 minutes, Monday-Friday. A dedicated circuit breaker for the 2 PM wall and meeting fatigue.',
    },
    {
      lead: 'Daily Pranayama',
      text: 'Structured breathwork tracks to actively down-regulate the nervous system between Zoom calls.',
    },
    {
      lead: 'Guided Meditation',
      text: 'Accessible mental clarity practices for sustained focus.',
    },
  ],
}

const CORPORATE_OFFLINE_ACCESS = {
  title: 'Corporate Offline Access: The Physical Sanctuaries',
  description:
    'Giving your employees a meditation app is not enough. To truly disconnect, they need a physical sanctuary to separate work from rest. We partner with premium physical spaces to offer your local teams drop-in access to curated restorative practices.',
  offerings: [
    { lead: "Yoga @ Away & St. John's", text: 'Dedicated physical movement rooms.' },
    { lead: 'Uruvana', text: 'Deep frequency corporate soundbath sessions.' },
    { lead: 'Ujjayi', text: 'Facilitated offline breathwork rooms.' },
  ],
}

const COHORT_TIMELINE = [
  {
    phase: 'Week 0',
    label: 'Baseline and Alignment',
    description: 'Define team goals and capture baseline wellbeing indicators.',
  },
  {
    phase: 'Weeks 1-5',
    label: 'Live Cohort Sessions',
    description: 'Run guided practices with clear weekly themes and continuity.',
  },
  {
    phase: 'Week 6',
    label: 'Outcomes and Next Sprint',
    description: 'Review progress, share insights, and plan the next cycle.',
  },
]

const COHORT_PROOF = [
  { metric: '92%', context: 'average weekly session attendance' },
]

const COHORT_QUALIFIERS = ['Works for teams of all sizes', 'Remote and hybrid friendly']

const TRUST_MARKERS = [
  {
    logoSrc: './corporate-lander/company-logos/junket.png',
    logoAlt: 'Company logo placeholder',
    description: 'Trusted by teams that prioritise employee wellbeing and sustainable high performance.',
  },
  {
    logoSrc: './corporate-lander/company-logos/theparklogo.png',
    logoAlt: 'Company logo placeholder',
    description: 'Used by growing organisations to build resilient teams and better workplace culture.',
  },
  {
    logoSrc: './corporate-lander/company-logos/away.svg',
    logoAlt: 'Company logo placeholder',
    description: 'Preferred for holistic programs that support focus, energy, and team alignment.',
  },
  {
    logoSrc: './corporate-lander/company-logos/build3logo.avif',
    logoAlt: 'Build3',
    description: 'Preferred for holistic programs that support focus, energy, and team alignment.',
  },
  {
    logoSrc: './corporate-lander/company-logos/geek.png',
    logoAlt: 'Geek Creative Agency',
    description: 'Preferred for holistic programs that support focus, energy, and team alignment.',
  },
  {
    logoSrc: './corporate-lander/company-logos/readtheroom.png',
    logoAlt: 'Read The Room',
    description: 'Preferred for holistic programs that support focus, energy, and team alignment.',
  },
]

const ConsultationModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', companyName: '', phone: '' })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError('')
    try {
      await axios.post(`${API_URL}/api/corporate-consultations`, form)
      setSubmitted(true)
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    onClose()
    setTimeout(() => {
      setForm({ name: '', email: '', companyName: '', phone: '' })
      setSubmitted(false)
      setError('')
    }, 300)
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
          <motion.div
            className="relative w-full max-w-md rounded-2xl border border-primary-100/60 bg-white p-6 shadow-2xl sm:p-8"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            <button
              onClick={handleClose}
              className="absolute right-4 top-4 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            {submitted ? (
              <div className="py-8 text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary-100">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-primary-700" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Thank you!</h3>
                <p className="mt-2 text-sm text-gray-600">
                  We&apos;ve received your consultation request. Our team will reach out to you shortly.
                </p>
                <button
                  onClick={handleClose}
                  className="mt-6 rounded-full bg-primary-700 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-800"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <h3 className="mb-1 text-xl font-semibold text-gray-900 sm:text-2xl">
                  Schedule a Consultation
                </h3>
                <p className="mb-6 text-sm text-gray-500">
                  Fill in your details and we&apos;ll get in touch to discuss your team&apos;s wellness needs.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="cons-name" className="mb-1 block text-sm font-medium text-gray-700">
                      Full Name
                    </label>
                    <input
                      id="cons-name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      placeholder=""
                    />
                  </div>
                  <div>
                    <label htmlFor="cons-email" className="mb-1 block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      id="cons-email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      placeholder=""
                    />
                  </div>
                  <div>
                    <label htmlFor="cons-company" className="mb-1 block text-sm font-medium text-gray-700">
                      Company Name
                    </label>
                    <input
                      id="cons-company"
                      name="companyName"
                      type="text"
                      required
                      value={form.companyName}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      placeholder=""
                    />
                  </div>
                  <div>
                    <label htmlFor="cons-phone" className="mb-1 block text-sm font-medium text-gray-700">
                      Phone Number
                    </label>
                    <input
                      id="cons-phone"
                      name="phone"
                      type="tel"
                      required
                      value={form.phone}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      placeholder="+919876543210"
                    />
                  </div>

                  {error && (
                    <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-full bg-primary-700 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const CorporateLanding = () => {
  const shouldReduceMotion = useReducedMotion()
  const [showConsultation, setShowConsultation] = useState(false)

  useEffect(() => { trackEvent('Page Viewed', { page: 'Corporates' }) }, [])

  const scrollToCorporateSuite = () => {
    document.getElementById('corporate-wellbeing-suite')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }
  const showHowItWorksImageBackground = HOW_IT_WORKS_BACKGROUND_MODE === 'image'
  const howItWorksSectionStyle = showHowItWorksImageBackground
    ? {
        backgroundImage: `linear-gradient(180deg, rgb(212, 202, 171, 0.6) 0%, rgb(212, 202, 171, 0.9) 100%), url(${HOW_IT_WORKS_BACKGROUND_IMAGE_URL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : undefined

  return (
    <main className="relative min-h-screen overflow-hidden bg-primary-300">
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 opacity-100"
        style={{
          backgroundImage: `url(${BACKGROUND_PATTERN_URL})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.8 }}
        transition={{ duration: 20, ease: 'easeOut', repeat: Infinity, repeatType: 'reverse' }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-br from-cream/88 via-cream/82 to-primary-50/75"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
      />
      <section className="relative z-10 overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <motion.div
            className="absolute -left-20 -top-24 h-56 w-56 rounded-full bg-cream/50 blur-3xl sm:h-72 sm:w-72 lg:h-96 lg:w-96"
            animate={
              shouldReduceMotion ? undefined : { x: [0, 34, 0], y: [0, 38, 0], scale: [1, 1.1, 1] }
            }
            transition={{ duration: 16, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute right-0 top-1/3 h-48 w-48 rounded-full bg-cream/80 blur-3xl sm:h-64 sm:w-64 lg:h-80 lg:w-80"
            animate={
              shouldReduceMotion ? undefined : { x: [-500, -100, 0], y: [0, -40, 0], scale: [1, 0.9, 1] }
            }
            transition={{ duration: 10, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute bottom-0 left-1/3 h-52 w-52 rounded-full bg-cream/50 blur-3xl sm:h-72 sm:w-72 lg:h-88 lg:w-88"
            animate={
              shouldReduceMotion ? undefined : { x: [0, 30, 0], y: [0, -34, 0], scale: [1, 1.08, 1] }
            }
            transition={{ duration: 17, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
          />
        </div>
        <div className="relative mx-auto grid min-h-screen max-w-7xl items-start gap-10 px-5 pt-20 sm:pt-24 pb-4 sm:pb-16 sm:px-8 lg:items-center gap-4 lg:grid-cols-2 lg:gap-14 lg:px-12 lg:py-20">
          <motion.a
            href="/"
            className="absolute inset-x-0 top-6 flex justify-center sm:top-7 lg:top-8"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            aria-label="Wellness Loop home"
          >
            <img src="/logo.png" alt="Wellness Loop Logo" className="h-12 sm:h-20 w-auto" />
          </motion.a>

          <motion.div
            className="order-2 lg:order-1"
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: 'easeOut' }}
          >
            <motion.div
              className="overflow-hidden rounded-3xl border border-primary-100/80 bg-white/70 shadow-xl backdrop-blur-sm"
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            >
              <img
                src={HERO_IMAGE_URL}
                alt="Corporate team participating in a workplace wellness session"
                className="h-[260px] w-full object-cover sm:h-[360px] lg:h-[520px]"
                loading="lazy"
              />
            </motion.div>
          </motion.div>

          <motion.div
            className="order-1 lg:order-2"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: 'easeOut' }}
          >
            <motion.p
              className="mb-4 inline-flex rounded-full border border-primary-200 bg-primary-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary-700 sm:text-sm"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease: 'easeOut' }}
            >
              Wellness Loop For Corporates
            </motion.p>
            <motion.h1
              className="max-w-xl text-4xl font-bold leading-tight tracking-tight text-gray-900 sm:text-5xl lg:text-6xl"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5, ease: 'easeOut' }}
            >
              Elevate Your Team. Cultivate Their Wellbeing.
            </motion.h1>
            <motion.p
              className="mt-2 sm:mt-6 max-w-xl text-base leading-relaxed text-gray-700 sm:text-xl"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65, ease: 'easeOut' }}
            >
              Holistic wellness programs designed to improve employee wellbeing, reduce burnout, boost focus, and create a mindspace where employees thrive.
            </motion.p>
            <div className="mt-4 sm:mt-8">
              <div className="inline-flex flex-col items-center gap-3">
                <motion.button
                  onClick={() => setShowConsultation(true)}
                  className="rounded-full bg-primary-700 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary-700/25 transition-colors hover:bg-primary-800 sm:text-base"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.8, ease: 'easeOut' }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Schedule a Consultation
                </motion.button>
                <button
                  type="button"
                  onClick={scrollToCorporateSuite}
                  className="group inline-flex items-center justify-center gap-2 border-0 bg-transparent p-0 text-center text-sm font-semibold text-primary-700 transition-colors duration-200 hover:text-primary-900 focus:outline-none sm:text-base"
                >
                  <span className="underline-offset-4 group-hover:underline">Know more</span>
                  <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="corporate-wellbeing-suite" className="relative z-10 border-t border-primary-100/60 bg-primary-900/90">
        <div className="mx-auto max-w-7xl px-2 py-16 sm:px-8 lg:px-12 lg:py-20">
          <motion.div
            className="mx-auto max-w-4xl text-center"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            <p className="mb-4 inline-flex rounded-full border border-primary-200/40 bg-primary-800/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-cream sm:text-sm">
              Corporate Wellbeing Suite
            </p>
            <h2 className="text-3xl font-semibold leading-tight text-cream sm:text-4xl lg:text-5xl">
              Holistic wellbeing programs for the full spectrum of your team&apos;s need
            </h2>
            <p className="mt-5 px-6 sm:px-0  text-justify sm:text-center text-base leading-relaxed text-primary-100 sm:text-lg">
              Our corporate suite covers physical movement, mental clarity, and team connection.
              With Wellness Loop, you have a dedicated partner in cultivating a thriving workplace.
            </p>
          </motion.div>

          <motion.article
            className="mt-12 overflow-hidden rounded-3xl border border-primary-100/30 bg-primary-800/85 shadow-xl"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <div className="grid gap-0 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="space-y-6 p-4 sm:p-8 lg:p-10">
                <p className="inline-flex rounded-full border border-primary-200/40 bg-primary-700/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-cream sm:text-sm">
                  Featured Program
                </p>
                <div>
                  <h3 className="text-3xl font-semibold leading-tight text-cream sm:text-4xl">
                  Energizers: A Leadership Performance Lab
                  </h3>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-primary-100 sm:text-base">
                    A 6-Week Leadership Performance Lab. Built specifically for decision-makers facing high cognitive load.
                  </p>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-primary-100 sm:text-base">
                    Constant context-switching degrades the prefrontal cortex. This structured online lab trains leaders to regulate their nervous systems under pressure, delivering a 2.87 average drop in team stress levels.
                  </p>
                  <p className="inline-flex border-primary-200/40 bg-primary-700/60 px-4 py-2 rounded-full mt-2 text-sm font-semibold text-cream sm:text-base items-center gap-2">
                    <CalendarClock className="h-4 w-4" aria-hidden="true" />
                    Next cohort starts on June 5th, 2026.
                  </p>
                  <div className="flex items-center gap-2">
                  <a
                    href={ENERGIZERS_PDF_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-cream underline underline-offset-4 transition hover:text-primary-100 sm:text-base"
                  >
                    <FileText className="h-4 w-4" aria-hidden="true" />
                    Download the ROI Pitch Deck
                  </a>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-200 sm:text-sm">
                    Featured track
                  </p> */}
                  <div className="rounded-2xl border border-primary-200/25 bg-primary-900/40 p-4 sm:p-5">
                    <p className="text-base font-semibold text-cream">{COHORT_FEATURED_FORMAT.title}</p>
                    <p className="mt-1 text-xs font-medium uppercase tracking-[0.08em] text-primary-200">
                      {COHORT_FEATURED_FORMAT.cadence}
                    </p>
                    <ul className="mt-3 space-y-2">
                      {COHORT_FEATURED_FORMAT.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-start gap-2 text-sm text-primary-100">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary-200" aria-hidden="true" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-200 sm:text-sm">
                    How it runs
                  </p>
                  <div className="mt-3 space-y-3">
                    {COHORT_TIMELINE.map((item) => (
                      <div
                        key={item.phase}
                        className="rounded-2xl border border-primary-200/20 bg-primary-900/35 p-3.5 sm:p-4"
                      >
                        <div className="flex items-center gap-2">
                          <CalendarClock className="h-4 w-4 text-primary-200" aria-hidden="true" />
                          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-primary-200">
                            {item.phase}
                          </p>
                        </div>
                        <p className="mt-2 text-base font-semibold text-cream">{item.label}</p>
                        <p className="mt-1 text-sm text-primary-100">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col border-t border-primary-100/20 bg-primary-900/55 lg:border-l lg:border-t-0">
                <img
                  src={COHORT_IMAGE_URL}
                  alt="Employees attending an online cohort wellbeing session"
                  className="rounded-2xl w-full object-contain px-2 pt-2"
                  loading="lazy"
                />
                <img
                  src={COHORT_IMAGE_URL2}
                  alt="Employees attending an online cohort wellbeing session"
                  className="rounded-2xl w-full object-contain px-2 pt-2"
                  loading="lazy"
                />
                <div className="space-y-5 p-6 sm:p-7">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-200 sm:text-sm">
                    Outcomes and trust
                  </p>
                  <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
                    {COHORT_PROOF.map((item) => (
                      <div
                        key={item.metric}
                        className="rounded-2xl border border-primary-200/25 bg-primary-800/70 p-4"
                      >
                        <div className="flex items-center gap-2 text-primary-100">
                          {item.metric === '6 weeks' ? (
                            <CalendarClock className="h-4 w-4" aria-hidden="true" />
                          ) : item.metric === 'Remote-ready' ? (
                            <Laptop className="h-4 w-4" aria-hidden="true" />
                          ) : (
                            <LineChart className="h-4 w-4" aria-hidden="true" />
                          )}
                          <p className="text-lg font-semibold text-cream">{item.metric}</p>
                        </div>
                        <p className="mt-1 text-sm text-primary-100">{item.context}</p>
                      </div>
                    ))}
                  </div>
                  <blockquote className="rounded-2xl border border-primary-200/25 bg-primary-800/60 p-4 text-md font-semibold leading-relaxed text-primary-100">
                    “The Energizer program is really working well. There are some fabulous speakers you&apos;re bringing in and the quality is just great And it&apos;s running like clockwork.”
                    <p className="text-base font-semibold text-primary-100 pt-2"> - Varun Chawla, Build3</p>
                  </blockquote>
                  
                </div>
              </div>
            </div>

            <div className="border-t border-primary-100/20 bg-primary-900/35 p-6 sm:p-8">
              <div className="rounded-2xl border border-primary-200/20 bg-primary-900/25 p-4 sm:p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-200/90 sm:text-sm">
                  Daily layer
                </p>
                <h4 className="mt-2 text-lg font-semibold text-cream sm:text-xl">{DIGITAL_SUITE.title}</h4>
                <p className="mt-3 text-sm leading-relaxed text-primary-100 sm:text-base">
                  {DIGITAL_SUITE.description}
                </p>
                <ul className="mt-3 space-y-2">
                  {DIGITAL_SUITE.offerings.map((item) => (
                    <li
                      key={item.lead}
                      className="flex items-start gap-2 text-xs text-primary-100/90 sm:text-sm"
                    >
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-200/80" aria-hidden="true" />
                      <span>
                        <span className="font-semibold text-cream">{item.lead}:</span>{' '}
                        {item.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-5 rounded-2xl border border-primary-200/20 bg-primary-900/25 p-4 sm:p-5">
                <h4 className="text-lg font-semibold text-cream sm:text-xl">{CORPORATE_OFFLINE_ACCESS.title}</h4>
                <p className="mt-3 text-sm leading-relaxed text-primary-100 sm:text-base">
                  {CORPORATE_OFFLINE_ACCESS.description}
                </p>
                <ul className="mt-3 space-y-2">
                  {CORPORATE_OFFLINE_ACCESS.offerings.map((item) => (
                    <li
                      key={item.lead}
                      className="flex items-start gap-2 text-xs text-primary-100/90 sm:text-sm"
                    >
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-200/80" aria-hidden="true" />
                      <span>
                        <span className="font-semibold text-cream">{item.lead}:</span>{' '}
                        {item.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-5 space-y-3">
                <motion.button
                  onClick={() => setShowConsultation(true)}
                  className="w-full rounded-full bg-cream px-6 py-3 text-sm font-semibold text-primary-900 transition-colors hover:bg-primary-50 sm:w-auto sm:text-base"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Get Cohort Plan for My Team
                </motion.button>
                <div className="flex flex-col gap-2 text-xs text-primary-100 sm:flex-row sm:gap-4 sm:text-sm">
                  {COHORT_QUALIFIERS.map((item) => (
                    <span key={item} className="inline-flex items-center gap-2">
                      <Users className="h-3.5 w-3.5 text-primary-200" aria-hidden="true" />
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.article>

          <div className="mt-10 text-center sm:text-left">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-200 sm:text-sm">
              Additional formats
            </p>
            <h3 className="mt-2 text-2xl font-semibold text-cream sm:text-3xl">
            Executive Offsites &amp; Clinical Masterclasses
            </h3>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <motion.article
              className="overflow-hidden rounded-3xl border border-primary-200/25 bg-primary-800/75 shadow-xl"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, ease: 'easeOut', delay: 0.02 }}
            >
              <img
                src={EVENTS_IMAGE_URL}
                alt="Corporate team participating in a themed wellbeing event"
                className="h-52 w-full object-cover sm:h-64"
                loading="lazy"
              />
              <div className="space-y-4 p-6 sm:p-7">
                <h3 className="text-2xl font-semibold text-cream">Themed Corporate Events &amp; Festivals</h3>
                <p className="text-sm text-primary-100 sm:text-base">
                  Curated experiences aligned with global observance days:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-primary-100 sm:text-base">
                  <li>International Women&apos;s Day</li>
                  <li>International Yoga Day</li>
                  <li>World Mental Health Day</li>
                  <li>World Disability Day</li>
                  <li>Founder / Leadership Retreat Days</li>
                </ul>
              </div>
            </motion.article>

            <motion.article
              className="overflow-hidden rounded-3xl border border-primary-200/25 bg-primary-800/75 shadow-xl"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, ease: 'easeOut', delay: 0.06 }}
            >
              <img
                src={OFFSITE_IMAGE_URL}
                alt="Team offsite retreat in a calm immersive venue"
                className="h-52 w-full object-cover sm:h-64"
                loading="lazy"
              />
              <div className="space-y-4 p-6 sm:p-7">
                <h3 className="text-2xl font-semibold text-cream">Offsites &amp; Immersive Retreats</h3>
                <p className="text-sm text-primary-100 sm:text-base">
                  For senior operators, the isolation of leadership requires specialized infrastructure. We design immersive, multi-modality journeys for cultural resets, strategic reflection, and profound nervous system restoration.
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm text-primary-100 sm:text-base">
                  <li>
                    <span className="font-semibold text-cream">Clinical Masterclasses:</span>{' '}
                    Evidence-based sessions (e.g., Longevity, Aging, and The Breath) in collaboration with medical institutions like St. John&apos;s.
                  </li>
                  <li>
                    <span className="font-semibold text-cream">Inanna Ki:</span>{' '}
                    An exclusive, restorative offsite addressing the invisible mental load of female founders and operators.
                  </li>
                  <li>
                    <span className="font-semibold text-cream">The Crossing:</span>{' '}
                    Immersive weekend retreats focusing on generational health, relational dynamics, and profound digital disconnection.
                  </li>
                </ul>
              </div>
            </motion.article>

            <motion.article
              className="overflow-hidden rounded-3xl border border-primary-200/25 bg-primary-800/75 shadow-xl"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, ease: 'easeOut', delay: 0.1 }}
            >
              <img
                src={LEADERSHIP_IMAGE_URL}
                alt="Leadership wellbeing strategy session"
                className="h-52 w-full object-cover sm:h-64"
                loading="lazy"
              />
              <div className="space-y-4 p-6 sm:p-7">
                <h3 className="text-2xl font-semibold text-cream">Leadership &amp; Founder Resilience</h3>
                <p className="text-sm text-primary-100 sm:text-base">
                  Targeted programs for decision-makers facing high cognitive and emotional load.
                </p>
                <div>
                  <p className="text-base font-semibold text-cream">Focus areas:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1 text-sm text-primary-100 sm:text-base">
                    <li>Stress resilience</li>
                    <li>Strategic clarity</li>
                    <li>Emotional regulation</li>
                    <li>Sustainable high performance</li>
                  </ul>
                </div>
              </div>
            </motion.article>

            <motion.article
              className="overflow-hidden rounded-3xl border border-primary-200/25 bg-primary-800/75 shadow-xl"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.65, ease: 'easeOut', delay: 0.14 }}
            >
              <img
                src={CUSTOM_PROGRAM_IMAGE_URL}
                alt="Custom corporate wellbeing program workshop"
                className="h-52 w-full object-cover sm:h-64"
                loading="lazy"
              />
              <div className="space-y-4 p-6 sm:p-7">
                <h3 className="text-2xl font-semibold text-cream">Custom Company Programs</h3>
                <p className="text-sm text-primary-100 sm:text-base">
                  Bespoke wellbeing programs designed around your organisation&apos;s culture, goals, and team dynamics.
                </p>
                <div>
                  <p className="text-base font-semibold text-cream">How it rolls out:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1 text-sm text-primary-100 sm:text-base">
                    <li>Proof of Concept (POC) with a focused pilot team</li>
                    <li>Feedback and outcome review to refine the model</li>
                    <li>Deeper integration across teams with ongoing support</li>
                  </ul>
                </div>
              </div>
            </motion.article>
          </div>
        </div>
      </section>
      
      <section
        className={`relative z-10 border-t border-primary-100/60 ${showHowItWorksImageBackground ? '' : 'bg-cream/80'}`}
        style={howItWorksSectionStyle}
      >
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-12 lg:py-20">
          <motion.div
            className="mx-auto max-w-3xl text-center"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <p className="mb-4 inline-flex rounded-full border border-primary-200 bg-primary-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary-700 sm:text-sm">
              The Process
            </p>
            <h2 className="text-3xl font-semibold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">
              How It Works
            </h2>
            <p className="mt-5 text-base font-semibold leading-relaxed text-gray-700 sm:text-lg">
              A frictionless, guided rollout for decision-makers to bring
              meaningful wellbeing experiences to their teams.
            </p>
          </motion.div>

          <div className="relative mt-12">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-12 hidden h-[2px] w-[calc(100%-10rem)] -translate-x-1/2 bg-primary-200 md:block"
            />
            <div className="grid gap-6 md:grid-cols-3">
              {PROCESS_STEPS.map((step, index) => {
                const Icon = step.icon
                return (
                  <motion.article
                    key={step.title}
                    className="relative rounded-3xl border border-primary-200/70 bg-white/90 p-6 shadow-lg shadow-primary-900/5 backdrop-blur-sm sm:p-7"
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{
                      duration: 0.6,
                      ease: 'easeOut',
                      delay: 0.06 * index,
                    }}
                  >
                    <div className="mb-5 flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full border border-primary-200 bg-primary-50 text-primary-700">
                        <Icon className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <span className="rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-primary-700">
                        Step {index + 1}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 sm:text-2xl">
                      {step.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-gray-600 sm:text-base">
                      {step.description}
                    </p>
                  </motion.article>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 border-t border-primary-100/60 bg-[#f4f6f8]">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-12 lg:py-20">
          <div className="mx-auto grid max-w-5xl items-center gap-8 lg:grid-cols-[1fr_300px] lg:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary-500 sm:text-xs">
                What This Means for Your Organisation
              </p>
              <h2 className="mt-3 max-w-lg text-2xl font-semibold leading-tight text-gray-900 sm:text-3xl lg:text-4xl">
                Employee wellbeing in practical terms.
              </h2>
              <ul className="mt-6 space-y-3 text-sm text-gray-700 sm:text-base font-semibold">
                {ORGANISATION_IMPACT_POINTS.map((point) => (
                  <li key={point.title} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-gray-700" aria-hidden="true" />
                    <span>{point.title}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-sm font-semibold uppercase tracking-[0.08em] text-primary-600 sm:text-base">
                results from our wellness program
              </p>
              <motion.div
                className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.6, ease: 'easeOut', delay: 0.08 }}
              >
                {ORGANISATION_STATS.map((stat) => {
                  const isBlueTone = stat.tone === 'blue'
                  const ArrowIcon = stat.trend === 'down' ? ArrowDown : ArrowUp

                  return (
                    <div
                      key={stat.label}
                      className={`flex h-full min-h-24 flex-col rounded-2xl border p-4 shadow-sm sm:min-h-[152px] sm:p-5 ${
                        isBlueTone
                          ? 'border-[#bad7e8] bg-[#edf6fb]'
                          : 'border-[#b9ddcf] bg-[#ecf8f3]'
                      }`}
                    >
                      <p className="min-h-[2.75rem] text-xs font-semibold uppercase tracking-[0.1em] text-[#4a6576] sm:min-h-[3rem]">
                        {stat.label}
                      </p>
                      <div className="mt-auto flex items-center justify-between gap-3">
                        <span className="text-3xl font-semibold leading-none text-[#1f3850]">
                          {stat.value}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                            stat.trend === 'down'
                              ? 'bg-[#d8e9f4] text-[#25506b]'
                              : 'bg-[#d4ede2] text-[#1f5a4a]'
                          }`}
                        >
                          <ArrowIcon className="h-3.5 w-3.5" aria-hidden="true" />
                          {stat.trend === 'down' ? 'Down' : 'Up'}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </motion.div>
            </motion.div>

            <motion.div
              className="mx-auto w-full max-w-[400px] overflow-hidden rounded-sm bg-black"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.65, ease: 'easeOut', delay: 0.06 }}
            >
              <img
                src={ORGANISATION_IMPACT_IMAGE_URL}
                alt="Close-up visual representing focus and performance"
                className="Aaspect-square w-full object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>

          <motion.p
            className="mx-auto mt-10 max-w-5xl text-center text-2xl font-bold text-gray-700 sm:text-2xl"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, ease: 'easeOut', delay: 0.1 }}
          >
            &ldquo;Wellbeing stops being a perk. It becomes performance infrastructure.&rdquo;
          </motion.p>
        </div>
      </section>
      <section className="relative z-10 border-t border-primary-100/60 bg-cream/90">
        <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 sm:py-12 lg:px-12 lg:py-14">
          <motion.div
            className="mx-auto max-w-4xl text-center"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary-500 sm:text-xs">
              Trusted By
            </p>
            <h2 className="mt-2 text-2xl font-semibold leading-tight text-gray-900 sm:text-3xl lg:text-4xl">
              Companies powered by Wellness Loop
            </h2>
          </motion.div>

          <div className="mx-auto mt-6 grid max-w-5xl grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TRUST_MARKERS.map((item, index) => (
              <motion.article
                key={`${item.logoSrc}-${index}`}
                className="p-1 text-center sm:p-2"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, ease: 'easeOut', delay: 0.06 * index }}
              >
                <div className="flex min-h-[68px] items-center justify-center px-2 py-2 sm:min-h-[74px] sm:py-3">
                  <img
                    src={item.logoSrc}
                    alt={item.logoAlt}
                    className="h-16 w-full max-w-[180px] object-contain"
                    loading="lazy"
                  />
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
      <section className="relative z-10 overflow-hidden border-t border-primary-100/60">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <motion.div
            className="absolute -left-20 -top-16 h-52 w-52 rounded-full bg-cream/55 blur-3xl sm:h-64 sm:w-64 lg:h-80 lg:w-80"
            animate={
              shouldReduceMotion ? undefined : { x: [0, 30, 0], y: [0, 36, 0], scale: [1, 1.08, 1] }
            }
            transition={{ duration: 16, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute right-0 top-1/4 h-48 w-48 rounded-full bg-cream/80 blur-3xl sm:h-60 sm:w-60 lg:h-72 lg:w-72"
            animate={
              shouldReduceMotion ? undefined : { x: [-40, 0, -40], y: [0, -34, 0], scale: [1, 0.92, 1] }
            }
            transition={{ duration: 12, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute -bottom-16 left-1/3 h-56 w-56 rounded-full bg-cream/55 blur-3xl sm:h-72 sm:w-72 lg:h-88 lg:w-88"
            animate={
              shouldReduceMotion ? undefined : { x: [0, 28, 0], y: [0, -30, 0], scale: [1, 1.06, 1] }
            }
            transition={{ duration: 17, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
          />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-12 lg:py-20">
          <motion.div
            className="mx-auto max-w-4xl overflow-hidden rounded-3xl border border-primary-100/80 bg-white/70 px-6 py-10 text-center shadow-xl backdrop-blur-sm sm:px-10 sm:py-12 lg:px-12 lg:py-14"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary-500 sm:text-xs">
              
            </p>
            <h2 className="mt-3 text-3xl font-semibold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">
              Ready to redesign your team&apos;s wellbeing?
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-gray-700 sm:text-lg">
              Get in touch with us to see how Wellness Loop can make a difference to your team.
            </p>
            <motion.button
              onClick={() => setShowConsultation(true)}
              className="mt-8 rounded-full bg-primary-700 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary-700/25 transition-colors hover:bg-primary-800 sm:text-base"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Book Your Consultation
            </motion.button>
          </motion.div>
        </div>
      </section>
      <ConsultationModal isOpen={showConsultation} onClose={() => setShowConsultation(false)} />
      <div className="relative z-10">
        <Footer />
      </div>
    </main>
  )
}

export default CorporateLanding
